import { useMemo, useState, type FC } from "react";
import { CreatableSelect, chakraComponents } from "chakra-react-select";

import { useDebounce } from "@uidotdev/usehooks";
import { useItemsQuery } from "@features/item/get";
import { useDataPage } from "@shared/lib/use-data-page";
import { itemFactory, type Item } from "@entities/item";
import { Text, VStack } from "@chakra-ui/react";

interface Props {
  value: Item | void;
  exclude?: Item[];
  onChange?: (value: number | void) => unknown;
  onChangeItem?: (item: Item | void) => unknown;
  getCreateNewItemLabel?: (inputValue: string) => string;
}

export const ItemSelect: FC<Props> = ({
  value,
  exclude,
  onChange,
  onChangeItem,
  getCreateNewItemLabel,
}) => {
  const [createdItems, setCreatedItems] = useState<Item[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue, 500);
  const { data: itemsPage } = useItemsQuery({
    filters: inputValue ? { search: debouncedInputValue } : {},
  });
  const { rows } = useDataPage(itemsPage);

  const options = useMemo<Item[]>(() => {
    const combinedOptions = [...rows, ...createdItems];

    if (!exclude) return combinedOptions;

    const excludeIds = exclude.map((item) => item.id);

    return combinedOptions.filter((item) => !excludeIds.includes(item.id));
  }, [exclude, rows, createdItems]);

  const getCreationLabel = (): string =>
    getCreateNewItemLabel?.(inputValue) ?? `Create ${inputValue}`;

  const onCreateItem = () => {
    const tmpItem = itemFactory().createFromName(inputValue, {
      id: Math.random() * -1,
      unitsInUOO: 1,
    });

    setCreatedItems((created) => [...created, tmpItem]);
    onChangeItem?.(tmpItem);
    onChange?.(tmpItem.id);
    setInputValue("");
  };

  return (
    <CreatableSelect<Item>
      value={value ?? null}
      inputValue={inputValue}
      options={options}
      onChange={(option) => {
        onChange?.(option?.id ?? void 0);
        onChangeItem?.(option ?? void 0);
      }}
      onCreateOption={onCreateItem}
      components={{
        Option: (props) => {
          const { isSelected, data } = props;
          const { name, invoiceDescription } = data;

          if (!data?.id) return <chakraComponents.Option {...props} />;

          return (
            <chakraComponents.Option {...props}>
              <VStack w="full" alignItems="flex-start">
                <Text>{name}</Text>
                <Text fontSize="xs" color={isSelected ? "gray.50" : "gray.700"}>
                  {invoiceDescription}
                </Text>
              </VStack>
            </chakraComponents.Option>
          );
        },
      }}
      getOptionValue={(item) => String(item.id)}
      onInputChange={(value) => setInputValue(value)}
      getOptionLabel={(item) => item.name ?? getCreationLabel()}
    />
  );
};
